import { useMemo, useState } from 'react';
import { Calendar } from 'lucide-react';
import { LiksDateRangeDialog } from '../../../components/liks/LiksDateRangeDialog/LiksDateRangeDialog';
import {
  formatLiksDateRangeLabel,
  type LiksDateDayRange,
} from '../../../components/liks/LiksDateRangeDialog/dateRangePicker';
import { LiksIconButton } from '../../../components/liks/LiksIconButton/LiksIconButton';
import { useLacTenantStatsSearchParams } from '../../../features/lac/hooks/useLacTenantStatsSearchParams';
import type { LacTenantOverviewDateRange } from '../../../types/lac';
import { useI18n } from '../../../i18n/useI18n';
import { dayToIsoRange } from '../../../utils/dateTimeUtils';
import { LacTenantStatsChatbotsSection } from '../LacTenantStatsChatbotsSection';
import { LacTenantStatsDocumentsTeaser } from '../LacTenantStatsDocumentsTeaser';
import { LacTenantStatsKnowledgeBasesSection } from '../LacTenantStatsKnowledgeBasesSection';
import { LacTenantStatsPlatformChatsSection } from '../LacTenantStatsPlatformChatsSection';
import './LacTenantOverviewStats.css';

type LacTenantOverviewStatsProps = {
  tenantName: string;
};

export function LacTenantOverviewStats({
  tenantName,
}: LacTenantOverviewStatsProps) {
  const { locale, translate } = useI18n();
  const { knowledgeBaseId, setKnowledgeBaseId } =
    useLacTenantStatsSearchParams();
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);

  const selectedDayRange = useMemo((): LiksDateDayRange | null => {
    if (fromDay === '' || toDay === '' || toDay < fromDay) {
      return null;
    }
    return { fromDay, toDay };
  }, [fromDay, toDay]);

  const dateRange = useMemo((): LacTenantOverviewDateRange | undefined => {
    if (selectedDayRange == null) {
      return undefined;
    }
    return dayToIsoRange(selectedDayRange.fromDay, selectedDayRange.toDay);
  }, [selectedDayRange]);

  const rangeLabel = formatLiksDateRangeLabel(
    selectedDayRange,
    locale,
    translate('common.dateRange.entireTime'),
  );

  const handleApplyDateRange = (range: LiksDateDayRange | null) => {
    if (range == null) {
      setFromDay('');
      setToDay('');
    } else {
      setFromDay(range.fromDay);
      setToDay(range.toDay);
    }
    setIsDateDialogOpen(false);
  };

  return (
    <div className="lac-tenant-stats liks-scrollbar">
      <div className="lac-tenant-stats__date-summary">
        <span className="lac-tenant-stats__date-label">{rangeLabel}</span>
        <LiksIconButton
          variant="outlined"
          aria-label={translate('common.dateRange.open')}
          onClick={() => setIsDateDialogOpen(true)}
        >
          <Calendar strokeWidth={2} aria-hidden />
        </LiksIconButton>
      </div>

      <LiksDateRangeDialog
        isOpen={isDateDialogOpen}
        fromDay={fromDay}
        toDay={toDay}
        onClose={() => setIsDateDialogOpen(false)}
        onApply={handleApplyDateRange}
      />

      <LacTenantStatsDocumentsTeaser
        tenantName={tenantName}
        dateRange={dateRange}
      />
      <LacTenantStatsKnowledgeBasesSection
        tenantName={tenantName}
        selectedKnowledgeBaseId={knowledgeBaseId}
        onSelectKnowledgeBaseId={setKnowledgeBaseId}
        dateRange={dateRange}
      />
      <LacTenantStatsChatbotsSection
        tenantName={tenantName}
        knowledgeBaseId={knowledgeBaseId}
        dateRange={dateRange}
      />
      <LacTenantStatsPlatformChatsSection
        tenantName={tenantName}
        knowledgeBaseId={knowledgeBaseId}
        dateRange={dateRange}
      />
    </div>
  );
}
